/**
 * O aviso fixo sobre o mapa no modo "Próximos dias": que é MODELO, não
 * medição; o que foi ocultado; rodada nova; o estado do passo (carregando,
 * fora da previsão, falha). O instante e a rodada ficam no chip do relógio
 * (`RelogioDaPrevisao`), no lugar da hora da imagem.
 *
 * Celular: uma faixa curta logo abaixo da linha do topo, entre o trilho de
 * camadas e a coluna de botões da direita (não cobre nenhum dos dois).
 * Desktop: no meio, abaixo da busca (com o cartão da previsão aberto, no meio
 * da parte do mapa que sobra à esquerda dele).
 */
import clsx from 'clsx';
import { SIDEBAR_WIDTH } from '../map/viewControl';
import { usePainelLateralAberto } from '../ui/hooks';
import { useDeitado } from '../ui/useMediaQuery';
import { SOBRE_MODELO_TEMPO, type ModeloTempo } from './api';
import { camadaDe } from './camadas';
import { LARGURA_DO_CARTAO } from './EntradaDaPrevisao';
import { fixarRodadas } from './motor';
import { RESUMO_OCULTAS } from './ocultar';
import { tempoEmUso, usePrevisaoStore } from './store';

export function SeloPrevisao(props: { mobile: boolean }) {
  const deitado = useDeitado();
  const modo = usePrevisaoStore((s) => s.modo);
  const variavel = usePrevisaoStore((s) => s.variavel);
  const fumaca = usePrevisaoStore((s) => s.fumaca);
  const cenario = usePrevisaoStore((s) => s.cenario);
  const indice = usePrevisaoStore((s) => s.indice);
  const noPasso = usePrevisaoStore((s) => s.noPasso);
  const rodadaNova = usePrevisaoStore((s) => s.rodadaNova);
  const tempo = usePrevisaoStore(tempoEmUso);
  const situacao = usePrevisaoStore((s) => s.situacao);
  // Desktop: o cartão é só da ficha do ponto (os ajustes ficam no painel lateral).
  const cartao = usePrevisaoStore((s) => !props.mobile && s.painel === 'ponto');
  const lateral = usePainelLateralAberto() && !props.mobile ? SIDEBAR_WIDTH : 0;
  // Celular deitado com a folha aberta (à direita): o aviso fica à esquerda dela.
  const folhaAoLado = usePrevisaoStore((s) => !!s.painel) && props.mobile && deitado;
  if (!modo) return null;
  // Desktop com o cartão da previsão aberto: no meio da parte livre, à esquerda dele (senão cobriria o cabeçalho do cartão).
  const ocupado = `(min(${LARGURA_DO_CARTAO}px, 100% - 72px) + 56px + ${lateral}px)`;
  const camada = camadaDe(variavel, fumaca, cenario, tempo);
  const estado =
    !indice && situacao.estado === 'error'
      ? 'Previsão indisponível agora — tentando de novo em alguns minutos.'
      : noPasso === 'carregando'
        ? 'Carregando…'
        : noPasso === 'falhou'
          ? 'Falha ao baixar este passo.'
          : noPasso === 'sem' && indice
            ? camada.modelo === 'cams'
              ? 'Sem previsão de fumaça para este horário (o CAMS vai até 5 dias).'
              : camada.modelo === 'wn2'
                ? 'Sem cenários para este horário (o WeatherNext 2 começa 6 h depois da rodada).'
                : 'Sem previsão para este horário.'
            : null;

  return (
    <div
      data-selo-previsao=""
      className="pointer-events-none absolute z-20 flex flex-col items-center gap-1"
      style={
        props.mobile
          ? // Abaixo da linha do topo (12 + 44 + 6 px), do trilho até a coluna da direita (12 + 44 + 8 px).
            { top: 'calc(env(safe-area-inset-top) + 62px)', left: 'var(--trilho-fim)', right: folhaAoLado ? 'calc(min(420px + env(safe-area-inset-right), 62%) + 8px)' : '64px' }
          : cartao
            ? { top: '56px', left: `calc(${lateral}px + (100% - ${ocupado}) / 2)`, transform: 'translateX(-50%)', maxWidth: `calc(100% - ${ocupado} - 2rem)` }
            : { top: '56px', left: '50%', transform: 'translateX(-50%)', maxWidth: 'calc(100% - 2rem)' }
      }
    >
      <div
        className={clsx(
          'rounded-full border border-violet-400/60 bg-ink-900/85 text-center text-ink-100 shadow-lg backdrop-blur',
          props.mobile ? 'px-2.5 py-0.5 text-[11px] leading-snug' : 'px-3 py-1 text-xs',
        )}
      >
        {/* Com o AIFS no mapa, o selo diz que é o modelo de IA (o relógio diz o nome inteiro). */}
        <span className="font-semibold text-violet-300">
          {camada.modelo !== 'ifs' && camada.modelo in SOBRE_MODELO_TEMPO
            ? `${SOBRE_MODELO_TEMPO[camada.modelo as ModeloTempo].curto}${SOBRE_MODELO_TEMPO[camada.modelo as ModeloTempo].ia ? ' (IA)' : ''}, não medição`
            : 'Modelo, não medição'}
        </span>
        {/* Celular: uma linha só; o que foi ocultado vai para o leitor de tela (e está no painel). */}
        {props.mobile ? <span className="sr-only"> · {RESUMO_OCULTAS}</span> : ` · ${RESUMO_OCULTAS}`}
        {/* Abaixo de 400 px a frase quebraria em duas linhas: fica só "Modelo, não medição". */}
        {props.mobile && <span className="max-[399px]:sr-only"> · observadas desligadas</span>}
      </div>
      {estado && <div className="rounded-full bg-ink-900/85 px-3 py-0.5 text-[11px] text-ink-200">{estado}</div>}
      {rodadaNova && (
        <button
          type="button"
          onClick={() => fixarRodadas(true)}
          className="pointer-events-auto min-h-9 rounded-full border border-violet-400/60 bg-violet-600/40 px-3 text-[11px] text-white hover:bg-violet-600/60 pointer-coarse:min-h-11"
        >
          Rodada nova disponível · atualizar
        </button>
      )}
    </div>
  );
}
